import { InsightType, PartyRole } from "@digits-graphql/frontend/graphql-bearer"
import { SvgAddTransaction } from "@digits-shared/components/SVGIcons/line/AddTransaction.svg"
import { SvgBank } from "@digits-shared/components/SVGIcons/line/Bank.svg"
import { SvgBuilding02 } from "@digits-shared/components/SVGIcons/line/Building02.svg"
import { SvgCalendarDollar01 } from "@digits-shared/components/SVGIcons/line/CalendarDollar01.svg"
import { SvgCoinsHand } from "@digits-shared/components/SVGIcons/line/CoinsHand.svg"
import { SvgCoinsHand02 } from "@digits-shared/components/SVGIcons/line/CoinsHand02.svg"
import { SvgCreditCard02 } from "@digits-shared/components/SVGIcons/line/CreditCard02.svg"
import { SvgDollarHand } from "@digits-shared/components/SVGIcons/line/DollarHand.svg"
import { SvgDollarRefresh01 } from "@digits-shared/components/SVGIcons/line/DollarRefresh01.svg"
import { SvgHandShakeDollar01 } from "@digits-shared/components/SVGIcons/line/HandShakeDollar01.svg"
import { SvgIou } from "@digits-shared/components/SVGIcons/line/Iou.svg"
import { SvgPackageCheck } from "@digits-shared/components/SVGIcons/line/PackageCheck.svg"
import { SvgPieChart05 } from "@digits-shared/components/SVGIcons/line/PieChart05.svg"
import { SvgRefreshCw05 } from "@digits-shared/components/SVGIcons/line/RefreshCw05.svg"
import { SvgSwitchHorizontal01 } from "@digits-shared/components/SVGIcons/line/SwitchHorizontal01.svg"
import { SvgTag01 } from "@digits-shared/components/SVGIcons/line/Tag01.svg"
import { SvgTagDollar } from "@digits-shared/components/SVGIcons/line/TagDollar.svg"
import { SvgTrendUp01 } from "@digits-shared/components/SVGIcons/line/TrendUp01.svg"
import { SvgUserCoins01 } from "@digits-shared/components/SVGIcons/line/UserCoins01.svg"
import { SvgUsers01 } from "@digits-shared/components/SVGIcons/line/Users01.svg"

const QUALIFIED_PREFIX = "com.digits.party.role." as const
type FullyQualifiedPartyRole = `${typeof QUALIFIED_PREFIX}${keyof typeof PartyRole}`

// Canonical source of mapping a PartyRole to its different associated types and strings.
//
// The Order of the fields controls the order in which they appear in the UI - order provided by Yass on 2025-02-26
export class FrontendPartyRole {
  static readonly Customer = new FrontendPartyRole({
    partyRole: PartyRole.EntityCustomerRole,
    urlKey: "customer",
    displayName: "Customer",
    pluralDisplayName: "Customers",
    SvgIcon: SvgTagDollar,
    insightType: InsightType.Income,
    showsBalanceSummary: false,
  })

  static readonly RevenuePartner = new FrontendPartyRole({
    partyRole: PartyRole.EntityRevenuePartnerRole,
    urlKey: "revenue-partner",
    displayName: "Revenue Partner",
    pluralDisplayName: "Revenue Partners",
    SvgIcon: SvgHandShakeDollar01,
    showsBalanceSummary: false,
  })

  static readonly Vendor = new FrontendPartyRole({
    partyRole: PartyRole.EntityVendorRole,
    urlKey: "vendor",
    displayName: "Vendor",
    pluralDisplayName: "Vendors",
    SvgIcon: SvgBuilding02,
    insightType: InsightType.Expenses,
    showsBalanceSummary: false,
  })

  static readonly AssetVendor = new FrontendPartyRole({
    partyRole: PartyRole.EntityAssetVendorRole,
    urlKey: "asset-vendor",
    displayName: "Vendor",
    pluralDisplayName: "Vendors",
    SvgIcon: SvgBuilding02,
    showsBalanceSummary: false,
  })

  static readonly PrepaidVendor = new FrontendPartyRole({
    partyRole: PartyRole.EntityPrepaidVendorRole,
    urlKey: "prepaid-vendor",
    displayName: "Prepaid Vendor",
    pluralDisplayName: "Prepaid Vendors",
    SvgIcon: SvgCalendarDollar01,
    insightType: InsightType.AssetsPrepaid,
    showsBalanceSummary: true,
  })

  static readonly Supplier = new FrontendPartyRole({
    partyRole: PartyRole.EntitySupplierRole,
    urlKey: "supplier",
    displayName: "Supplier",
    pluralDisplayName: "Suppliers",
    SvgIcon: SvgPackageCheck,
    insightType: InsightType.CostOfGoodsSold,
    showsBalanceSummary: false,
  })

  static readonly Asset = new FrontendPartyRole({
    partyRole: PartyRole.EntityAssetRole,
    urlKey: "asset",
    displayName: "Supplier",
    pluralDisplayName: "Suppliers",
    SvgIcon: SvgPackageCheck,
    showsBalanceSummary: false,
  })

  static readonly AssetSupplier = new FrontendPartyRole({
    partyRole: PartyRole.EntityAssetSupplierRole,
    urlKey: "asset-supplier",
    displayName: "Supplier",
    pluralDisplayName: "Suppliers",
    SvgIcon: SvgPackageCheck,
    showsBalanceSummary: false,
  })

  static readonly OtherIncome = new FrontendPartyRole({
    partyRole: PartyRole.EntityOtherIncomeRole,
    urlKey: "other-income",
    displayName: "Other Income Source",
    pluralDisplayName: "Other Income Sources",
    SvgIcon: SvgPieChart05,
    insightType: InsightType.Income,
    showsBalanceSummary: false,
  })

  static readonly OtherExpenses = new FrontendPartyRole({
    partyRole: PartyRole.EntityOtherExpensesRole,
    urlKey: "other-expenses",
    displayName: "Other Expense Source",
    pluralDisplayName: "Other Expense Sources",
    SvgIcon: SvgAddTransaction,
    insightType: InsightType.Expenses,
    showsBalanceSummary: false,
  })

  static readonly UnpaidCustomer = new FrontendPartyRole({
    partyRole: PartyRole.EntityUnpaidCustomerRole,
    urlKey: "receivable",
    displayName: "Unpaid Customer",
    pluralDisplayName: "Unpaid Customers",
    SvgIcon: SvgCoinsHand,
    insightType: InsightType.AssetsReceivable,
    showsBalanceSummary: true,
  })

  static readonly Debtor = new FrontendPartyRole({
    partyRole: PartyRole.EntityDebtorRole,
    urlKey: "debtor",
    displayName: "Debtor",
    pluralDisplayName: "Debtors",
    SvgIcon: SvgDollarHand,
    showsBalanceSummary: false,
  })

  static readonly Investee = new FrontendPartyRole({
    partyRole: PartyRole.EntityInvesteeRole,
    urlKey: "investee",
    displayName: "Investee",
    pluralDisplayName: "Investees",
    SvgIcon: SvgTrendUp01,
    showsBalanceSummary: false,
  })

  static readonly OwedVendor = new FrontendPartyRole({
    partyRole: PartyRole.EntityOwedVendorRole,
    urlKey: "payable",
    displayName: "Owed Vendor",
    pluralDisplayName: "Owed Vendors",
    SvgIcon: SvgCoinsHand02,
    insightType: InsightType.LiabilitiesPayable,
    showsBalanceSummary: true,
  })

  static readonly Lender = new FrontendPartyRole({
    partyRole: PartyRole.EntityLenderRole,
    urlKey: "lender",
    displayName: "Lender",
    pluralDisplayName: "Lenders",
    SvgIcon: SvgIou,
    insightType: InsightType.LiabilitiesLongTerm,
    showsBalanceSummary: true,
  })

  static readonly Liabilities = new FrontendPartyRole({
    partyRole: PartyRole.EntityLiabilitiesRole,
    urlKey: "liability",
    displayName: "Lender",
    pluralDisplayName: "Lenders",
    SvgIcon: SvgIou,
    showsBalanceSummary: false,
  })

  static readonly OtherLiabilities = new FrontendPartyRole({
    partyRole: PartyRole.EntityOtherLiabilitiesRole,
    urlKey: "other-liabilities",
    displayName: "Other Liability",
    pluralDisplayName: "Other Liabilities",
    SvgIcon: SvgIou,
    showsBalanceSummary: false,
  })

  static readonly Shareholder = new FrontendPartyRole({
    partyRole: PartyRole.EntityShareholderRole,
    urlKey: "shareholder",
    displayName: "Shareholder",
    pluralDisplayName: "Shareholders",
    SvgIcon: SvgUserCoins01,
    insightType: InsightType.Equity,
    showsBalanceSummary: true,
  })

  static readonly Processor = new FrontendPartyRole({
    partyRole: PartyRole.EntityProcessorRole,
    urlKey: "processor",
    displayName: "Processor",
    pluralDisplayName: "Processors",
    SvgIcon: SvgSwitchHorizontal01,
    showsBalanceSummary: false,
  })

  static readonly CreditCards = new FrontendPartyRole({
    partyRole: PartyRole.EntityCreditCardsRole,
    urlKey: "credit-cards",
    displayName: "Credit Card",
    pluralDisplayName: "Credit Cards",
    SvgIcon: SvgCreditCard02,
    showsBalanceSummary: false,
  })

  static readonly Banks = new FrontendPartyRole({
    partyRole: PartyRole.EntityBanksRole,
    urlKey: "banks",
    displayName: "Bank",
    pluralDisplayName: "Banks",
    SvgIcon: SvgBank,
    showsBalanceSummary: false,
  })

  static readonly CreditInstitution = new FrontendPartyRole({
    partyRole: PartyRole.InstitutionCreditRole,
    urlKey: "credit-institution",
    displayName: "Credit Institution",
    pluralDisplayName: "Credit Institutions",
    SvgIcon: SvgCreditCard02,
    showsBalanceSummary: false,
  })

  static readonly DebitInstitution = new FrontendPartyRole({
    partyRole: PartyRole.InstitutionDebitRole,
    urlKey: "debit-institution",
    displayName: "Debit Institution",
    pluralDisplayName: "Debit Institutions",
    SvgIcon: SvgBank,
    showsBalanceSummary: false,
  })

  static readonly Facilitator = new FrontendPartyRole({
    partyRole: PartyRole.FacilitatorRole,
    urlKey: "facilitator",
    displayName: "Facilitator",
    pluralDisplayName: "Facilitator",
    SvgIcon: SvgDollarRefresh01,
    showsBalanceSummary: false,
  })

  static readonly PaymentProcessor = new FrontendPartyRole({
    partyRole: PartyRole.PaymentProcessorRole,
    urlKey: "payment-processor",
    displayName: "Payment Processor",
    pluralDisplayName: "Payment Processors",
    SvgIcon: SvgRefreshCw05,
    showsBalanceSummary: false,
  })

  static readonly Owner = new FrontendPartyRole({
    partyRole: PartyRole.OwnerRole,
    urlKey: "owner",
    displayName: "Owner",
    pluralDisplayName: "Owners",
    SvgIcon: SvgUsers01,
    showsBalanceSummary: false,
  })

  static readonly Unspecified = new FrontendPartyRole({
    partyRole: PartyRole.EntityUnspecifiedRole,
    urlKey: "unspecified",
    displayName: "Unspecified",
    pluralDisplayName: "Unspecified",
    SvgIcon: SvgTag01,
    showsBalanceSummary: false,
  })

  static readonly Unknown = new FrontendPartyRole({
    partyRole: PartyRole.UnknownRole,
    urlKey: "unknown",
    displayName: "Unknown",
    pluralDisplayName: "Unknown",
    SvgIcon: SvgTag01,
    showsBalanceSummary: false,
  })
  readonly partyRole: PartyRole
  readonly urlKey: string
  readonly displayName: string
  readonly pluralDisplayName: string
  readonly SvgIcon: React.FC
  readonly insightType?: InsightType
  readonly showsBalanceSummary: boolean

  constructor({
    partyRole,
    urlKey,
    displayName,
    pluralDisplayName,
    SvgIcon,
    insightType,
    showsBalanceSummary,
  }: {
    partyRole: PartyRole
    urlKey: string
    displayName: string
    pluralDisplayName: string
    SvgIcon: React.FC
    insightType?: InsightType
    showsBalanceSummary: boolean
  }) {
    this.partyRole = partyRole
    this.urlKey = urlKey
    this.displayName = displayName
    this.pluralDisplayName = pluralDisplayName
    this.SvgIcon = SvgIcon
    this.insightType = insightType
    this.showsBalanceSummary = showsBalanceSummary
  }

  static all(): FrontendPartyRole[] {
    return Object.values(FrontendPartyRole)
  }

  static findByURLKey(urlKey?: string): FrontendPartyRole {
    return this.all().find((role) => role.urlKey === urlKey) || FrontendPartyRole.Unknown
  }

  static findByRole(partyRole?: PartyRole | null): FrontendPartyRole {
    return this.all().find((role) => role.partyRole === partyRole) || FrontendPartyRole.Unknown
  }

  static findByFullyQualifiedPartyRole(fullyQualifiedPartyRole?: string | null) {
    const role =
      (fullyQualifiedPartyRole?.substring(QUALIFIED_PREFIX.length) as PartyRole) ||
      PartyRole.UnknownRole

    return FrontendPartyRole.findByRole(
      fullyQualifiedPartyRole?.indexOf(QUALIFIED_PREFIX) === 0
        ? PartyRole[role]
        : PartyRole.UnknownRole
    )
  }

  static findByInsightType(insightType?: InsightType): FrontendPartyRole {
    return this.all().find((role) => role.insightType === insightType) || FrontendPartyRole.Unknown
  }

  static displayStringForRole(role?: PartyRole, plural = false) {
    const feRole = FrontendPartyRole.findByRole(role)
    return plural ? feRole.pluralDisplayName : feRole.displayName
  }

  static roleURLKey(role?: PartyRole | null): string {
    return FrontendPartyRole.findByRole(role).urlKey
  }

  static fullyQualifiedPartyRole(pr?: PartyRole): FullyQualifiedPartyRole | undefined {
    if (pr) {
      return `${QUALIFIED_PREFIX}${pr}`
    }
    return undefined
  }

  static extractPartyDetailsParams(s?: string | null) {
    const { partyRole } = FrontendPartyRole.findByFullyQualifiedPartyRole(s)
    return {
      partyRole,
    }
  }
}

const UNSUPPORTED_PARTY_ROLES: Set<FrontendPartyRole> = new Set([
  FrontendPartyRole.Processor,
  FrontendPartyRole.Banks,
  FrontendPartyRole.CreditCards,
])

export const SUPPORTED_FRONTEND_PARTY_ROLES = FrontendPartyRole.all().filter(
  (r) => !UNSUPPORTED_PARTY_ROLES.has(r)
)

export const SUPPORTED_PARTY_ROLES: PartyRole[] = SUPPORTED_FRONTEND_PARTY_ROLES.map(
  (fRole) => fRole.partyRole
)
export const SUPPORTED_PARTY_ROLES_URL_KEYS: string[] = SUPPORTED_FRONTEND_PARTY_ROLES.map(
  (fRole) => fRole.urlKey
)

const VENDOR_DETAIL_PARTY_ROLES = new Set([
  PartyRole.EntityVendorRole,
  PartyRole.EntitySupplierRole,
  PartyRole.EntityAssetVendorRole,
  PartyRole.EntityPrepaidVendorRole,
  PartyRole.EntityOtherExpensesRole,
  PartyRole.EntityOwedVendorRole,
])

const CUSTOMER_DETAIL_PARTY_ROLES = new Set([
  PartyRole.EntityCustomerRole,
  PartyRole.EntityRevenuePartnerRole,
  PartyRole.EntityUnpaidCustomerRole,
])

export function partyDetailRole(partyRole: PartyRole) {
  if (VENDOR_DETAIL_PARTY_ROLES.has(partyRole)) return FrontendPartyRole.Vendor
  if (CUSTOMER_DETAIL_PARTY_ROLES.has(partyRole)) return FrontendPartyRole.Customer
  return FrontendPartyRole.findByRole(partyRole)
}

export function partyDetailURLKey(partyRole: PartyRole | undefined) {
  return partyRole ? partyDetailRole(partyRole).urlKey : FrontendPartyRole.Unknown.urlKey
}

// The role a party opens on when it has several: the first in display order, never Unknown.
export function primaryPartyRole(roles: readonly PartyRole[]) {
  return SUPPORTED_PARTY_ROLES.find(
    (role) => role !== PartyRole.UnknownRole && roles.includes(role)
  )
}
